import { useRef, useState } from "react";
import { Camera, Loader2 } from "lucide-react";
import { Avatar, useToast } from "@/components/ui";
import { api, ApiError } from "@/lib/api";
import { fullName } from "@/lib/format";
import type { Contact } from "@/lib/types";
import { cn } from "@/lib/utils";

/**
 * Click-to-upload avatar. Opens a file picker, presigns an upload URL via
 * the SyncSuite Services R2 layer, PUTs the bytes directly to R2, then
 * patches the contact with the public URL. The CRM never proxies the
 * file body itself — saves bandwidth and keeps Express from buffering
 * megabyte uploads.
 */
export function ContactAvatarUpload({
  contact, onSaved, size = "xl",
}: {
  contact: Contact;
  onSaved: (next: Contact) => void;
  size?: "lg" | "xl";
}) {
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const onPick = async (file: File) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please pick an image"); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("Image must be under 8 MB"); return; }
    setUploading(true);
    try {
      // Sanitize filename + scope the path under the contact id so each
      // contact's avatars stay grouped in R2. The mothership presigner
      // takes the last segment as the filename and the rest as the folder.
      const ext = file.name.includes(".") ? file.name.slice(file.name.lastIndexOf(".") + 1).toLowerCase() : "bin";
      const safeName = `avatar-${Date.now()}.${ext}`;
      const path = `contacts/${contact.id}/${safeName}`;

      // 1) ask the backend for a signed PUT URL — sizeBytes + contentType
      //    are bound into the signature so the actual PUT can't lie.
      const presign = await api.post<{
        fileId: string;
        uploadUrl: string;
        requiredHeaders: Record<string, string>;
        publicUrl: string;
      }>("/uploads/presign", {
        contentType: file.type,
        sizeBytes: file.size,
        path,
        bucket: "public",
      });

      // 2) PUT the bytes straight to R2 with whatever headers the signer
      //    requires — usually just Content-Type.
      const put = await fetch(presign.uploadUrl, {
        method: "PUT",
        headers: presign.requiredHeaders ?? { "Content-Type": file.type },
        body: file,
      });
      if (!put.ok) throw new Error(`Upload failed: ${put.status}`);

      // 3) Tell mothership the upload landed (fire-and-forget — even if
      //    confirm fails the bytes are still in R2 and addressable).
      void api.post("/uploads/confirm", { fileId: presign.fileId }).catch(() => {});

      // 4) Save the public URL on the contact row.
      const r = await api.patch<{ contact: Contact }>(`/contacts/${contact.id}`, {
        avatar_url: presign.publicUrl,
      });
      onSaved(r.contact);
      toast.success("Photo updated");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        title="Change photo"
        aria-label="Change photo"
        className={cn(
          "group relative inline-block rounded-full ring-zinc-700 transition-shadow",
          "hover:ring-2 hover:ring-indigo-500/40 focus-visible:ring-2 focus-visible:ring-indigo-500",
        )}
      >
        <Avatar
          name={fullName(contact)}
          email={contact.email}
          src={contact.avatar_url}
          size={size}
        />
        <span
          className={cn(
            "absolute inset-0 flex items-center justify-center rounded-full bg-black/55 opacity-0 transition-opacity",
            "group-hover:opacity-100",
            uploading && "opacity-100",
          )}
        >
          {uploading ? (
            <Loader2 className="h-4 w-4 animate-spin text-white" />
          ) : (
            <Camera className="h-4 w-4 text-white" />
          )}
        </span>
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) void onPick(f); }}
      />
    </div>
  );
}
